<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiArrowRight, mdiBookOpenPageVariantOutline } from "@mdi/js"
  import { learningBasePath } from "../../../../../../../../subviews/Learning/config"
  import { spaLink } from "../../../../../../../../../../_shared/_logic/navigation"
  import { getLearningProgress } from "../../../../../../../../_shared/_logic/learning-progress"
  import {
    getLearningPathProgress,
    getLearningLessonHref,
  } from "../../../../../../../../_shared/_logic/learning-path"

  const { organizationSlug } = $props<{ organizationSlug: string }>()
  const learningProgress = getLearningProgress()
  const course = $derived(learningProgress.course)
  const basePath = $derived(learningBasePath(organizationSlug))
  const progress = $derived(course ? getLearningPathProgress(course.lessons) : null)
  const next = $derived(progress?.nextLesson ?? null)
  const completed = $derived(
    course !== null && course.total_count > 0 && course.completed_count >= course.total_count,
  )
  const href = $derived(
    next
      ? getLearningLessonHref(
          basePath,
          next,
          progress?.nextMilestone?.key === "einrichtung" ? "?lernweg=einrichtung" : "",
        )
      : basePath,
  )
</script>

{#if completed}
  <div class="guide-reference-block">
  <a class="guide-reference" href={basePath} use:spaLink>
    <Icon path={mdiBookOpenPageVariantOutline} size="1.2rem" />
    Anleitungen nachschlagen
  </a>
  {@render loadError()}
  </div>
{:else}
  <section class="tutorial-panel" aria-labelledby="home-learning-heading">
    <h2 id="home-learning-heading">KehrBlatt Schritt für Schritt kennenlernen</h2>
    {#if course}
      <div class="guide-progress">
        <p class="guide-count">
          {course.completed_count} von {course.total_count} Anleitungen erledigt
        </p>
        <progress
          max={course.total_count}
          value={course.completed_count}
          aria-label="Persönlicher Lernfortschritt"
        ></progress>
      </div>
      {#if next}
        <p class="guide-next"><span>Als Nächstes</span><strong>{next.title}</strong></p>
      {/if}
      <div class="guide-actions">
        <a class="guide-continue" use:spaLink {href}>
          <Icon path={mdiArrowRight} size="1.2rem" />Weiterlernen
        </a>
        <a class="guide-overview" href={basePath} use:spaLink>Alle Anleitungen</a>
      </div>
    {:else if !learningProgress.error}
      <p role="status">Anleitungen werden geladen …</p>
    {/if}
    {@render loadError()}
  </section>
{/if}

{#snippet loadError()}
  {#if learningProgress.error}
    <div class="guide-load-error" role="alert">
      <span>{learningProgress.error}</span>
      <CTA
        type="secondaryButton"
        small={true}
        content="Erneut laden"
        darkMode={false}
        onClick={() => void learningProgress.loadCourse()}
      />
    </div>
  {/if}
{/snippet}

<style>
  .guide-reference-block {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .guide-load-error {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    color: var(--text-invers-100);
    line-height: 1.5;
  }

  .tutorial-panel {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.1rem;
    width: 100%;
    box-sizing: border-box;
    padding: 1.5rem;
    border-left: 5px solid var(--primary-100);
    background: color-mix(in srgb, var(--primary-100) 5%, var(--neutral-white));
    color: var(--text-invers-100);
  }

  h2 {
    margin: 0;
    font-size: 1.4rem;
    line-height: 1.35;
    color: inherit;
  }

  p {
    margin: 0;
    line-height: 1.5;
  }

  .guide-progress {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  .guide-count {
    font-weight: 600;
  }

  .guide-next {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .guide-next span {
    font-size: 0.9rem;
  }

  .guide-next strong {
    font-size: 1.1rem;
    line-height: 1.4;
  }

  .guide-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
  }

  .guide-overview,
  .guide-reference {
    color: var(--text-invers-100);
    text-underline-offset: 3px;
    line-height: 1.5;
    padding-block: 0.6rem;
  }

  .guide-reference,
  .guide-continue {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .guide-continue {
    min-height: 44px;
    box-sizing: border-box;
    padding: 0.65rem 1rem;
    color: var(--neutral-white);
    background: var(--primary-100);
    text-decoration: none;
    font-weight: 700;
  }

  .guide-continue:hover {
    background: color-mix(in srgb, var(--primary-100) 85%, black);
  }

  a:focus-visible {
    outline: 3px solid var(--text-invers-100);
    outline-offset: 3px;
  }

  progress {
    appearance: none;
    width: 100%;
    height: 0.5rem;
    border: 0;
    background: #e8ecee;
    color: #28794a;
  }

  progress::-webkit-progress-bar {
    background: #e8ecee;
  }

  progress::-webkit-progress-value {
    background: #28794a;
  }

  progress::-moz-progress-bar {
    background: #28794a;
  }

  @media (max-width: 600px) {
    .tutorial-panel {
      padding: 1rem;
    }
  }
</style>
